import { ActionType } from 'typesafe-actions';
import { select, put, takeEvery } from 'redux-saga/effects';
import { omit } from 'ramda';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IAnalyticsResponse } from 'APIPackage/AnalyticsModule/interfaces/IAnalyticsResponse';
import {
  fetchAnalytics,
  fetchAnalyticsKnm,
  exportAnalytics,
  exportAnalyticsKnm,
} from 'APIPackage/AnalyticsModule/services/analyticsApi';
import { IAnalyticsData } from 'APIPackage/AnalyticsModule/interfaces/IAnalyticsData';
import { IKnmIndexPage } from 'APIPackage/KnmModule/interfaces/IKnmIndexPage';
import { notifyExportError, notifyExportSuccess } from 'CorePackage/MainModule/sagas/commonNotificationSagas';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { mapRawToData } from 'KnmListPackage/MainModule/common/mapRawToData';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  analyticsSetData,
  analyticsKnmSetData,
  analyticsExport,
  analyticsKnmExport,
  analyticsGetData,
} from '../actions/analyticsActions';
import { AnalyticsActionsEnum } from '../enums/AnalyticsActionsEnum';

export function* watchAnalyticsSaga() {
  yield takeEvery(AnalyticsActionsEnum.ANALYTICS_GET_DATA, fetchAnalyticsSaga);
  yield takeEvery(AnalyticsActionsEnum.ANALYTICS_EXPORT, exportAnalyticsSaga);
  yield takeEvery(AnalyticsActionsEnum.ANALYTICS_KNM_GET_DATA, fetchAnalyticsKnmSaga);
  yield takeEvery(AnalyticsActionsEnum.ANALYTICS_KNM_GET_DATA_NEXT_PAGE, fetchAnalyticsKnmNextPageSaga);
  yield takeEvery(AnalyticsActionsEnum.ANALYTICS_KNM_EXPORT, exportAnalyticsKnmSaga);
  yield takeEvery(AnalyticsActionsEnum.ANALYTICS_INIT, analyticsInitSaga);
}

function* analyticsInitSaga() {
  yield put(analyticsGetData());
  yield put(catalogGet(CommonCatalogsNamesEnum.USER_KNOS));
}

function* fetchAnalyticsSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const { filter } = state.analytics;
    const res: IAnalyticsResponse = yield fetchAnalytics(filter);
    const data: IAnalyticsData[] = [omit(['knoAnalyticsDTOList'], res), ...res.knoAnalyticsDTOList];

    yield put(analyticsSetData(data));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* exportAnalyticsSaga(action: ActionType<typeof analyticsExport>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const { filter } = state.analytics;
    const { format } = action.payload;
    const meta: IExport = yield exportAnalytics(filter, format);

    yield notifyExportSuccess(meta.id);
  }
  function* funcCatch() {
    yield notifyExportError();
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

function* fetchAnalyticsKnmSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const { knmFilter, knmOrder } = state.analytics;
    const res: IKnmIndexPage = yield fetchAnalyticsKnm(knmFilter, 0, knmOrder);

    yield put(analyticsKnmSetData(mapRawToData(res.list, false), res.page, res.pageCount, res.totalCount));
  }
  yield sagaWrapper({
    funcTry,
  });
}

function* fetchAnalyticsKnmNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const { knmFilter, knmPage, knmData, knmOrder } = state.analytics;
    const res: IKnmIndexPage = yield fetchAnalyticsKnm(knmFilter, knmPage + 1, knmOrder);

    yield put(
      analyticsKnmSetData([...knmData, ...mapRawToData(res.list, false)], res.page, res.pageCount, res.totalCount),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* exportAnalyticsKnmSaga(action: ActionType<typeof analyticsKnmExport>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const { knmFilter } = state.analytics;
    const { format } = action.payload;
    const meta: IExport = yield exportAnalyticsKnm(knmFilter, format);

    yield notifyExportSuccess(meta.id);
  }
  function* funcCatch() {
    yield notifyExportError();
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}
